import { Button, ColumnTable } from '@octano/global-ui';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, UncontrolledTooltip } from 'reactstrap';

import { moneyFormatter } from '../../../../utils/currency';
import { DebtPublicationRun } from '../api';

const dictPrefix = 'financialPortal.debtPublication';

// Aligned with the color usage in StudentList's useStudentListColumns
// (STATUS_COLORS pattern) — green for a completed run, red for a failed
// one, yellow for a dry run (no file actually published).
const STATUS_COLORS: Record<DebtPublicationRun['status'], string> = {
  success: '#CEF0DA',
  failed: '#F2C0BD',
  dry_run: '#FFE6A1',
};

interface Options {
  onDownload: (row: DebtPublicationRun) => void;
}

export function useDebtPublicationColumns({
  onDownload,
}: Options): ColumnTable<DebtPublicationRun>[] {
  const { t } = useTranslation();
  const formatter = useMemo(() => moneyFormatter(), []);

  return useMemo<ColumnTable<DebtPublicationRun>[]>(
    () => [
      {
        columnName: 'startedAt',
        headerText: t(`${dictPrefix}.columns.startedAt`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ value }) =>
          value ? dayjs(value as string).format('DD/MM/YYYY HH:mm') : '-',
      },
      {
        columnName: 'trigger',
        headerText: t(`${dictPrefix}.columns.trigger`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ value }) => t(`${dictPrefix}.trigger.${value}`),
      },
      {
        columnName: 'provider',
        headerText: t(`${dictPrefix}.columns.provider`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        columnName: 'fileName',
        headerText: t(`${dictPrefix}.columns.fileName`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ value }) => (value as string | null) ?? '-',
      },
      {
        columnName: 'rowsOpen',
        headerText: t(`${dictPrefix}.columns.rowsOpen`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        columnName: 'rowsClosed',
        headerText: t(`${dictPrefix}.columns.rowsClosed`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        columnName: 'rowsVoided',
        headerText: t(`${dictPrefix}.columns.rowsVoided`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        columnName: 'rowsExcluded',
        headerText: t(`${dictPrefix}.columns.rowsExcluded`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        columnName: 'totalAmount',
        headerText: t(`${dictPrefix}.columns.totalAmount`),
        thClassName: 'text-right',
        tdClassName: 'text-right',
        cellFormat: ({ value }) => formatter.format(Number(value) || 0),
      },
      {
        columnName: 'status',
        headerText: t(`${dictPrefix}.columns.status`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => (
          <>
            <Badge
              id={`debt-publication-run-status-${row.id}`}
              pill
              className="fs-16 fw-400 py-2 px-4 text-dark"
              style={{
                backgroundColor: STATUS_COLORS[row.status],
                whiteSpace: 'nowrap',
              }}
            >
              {t(`${dictPrefix}.status.${row.status}`)}
            </Badge>
            {row.status === 'failed' && row.errorDetail ? (
              <UncontrolledTooltip
                target={`debt-publication-run-status-${row.id}`}
              >
                {row.errorDetail}
              </UncontrolledTooltip>
            ) : null}
          </>
        ),
      },
      {
        columnName: 'id',
        headerText: '',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        width: 96,
        cellFormat: ({ row }) =>
          row.fileName ? (
            <Button
              size="sm"
              icon="download"
              outlined
              text={t(`${dictPrefix}.download`)}
              onClick={() => onDownload(row)}
            />
          ) : null,
      },
    ],
    [t, formatter, onDownload],
  );
}
